import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

import { ValidationService } from '../../../core/services/validation/validation.service';
import { FormErrors } from '../../configs/form-errors.config';
import { ViewSample } from '../../configs/view-sample.config';
import { CustomError } from '../../models/api-response';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnChanges {
  @Output() remove: EventEmitter<void> = new EventEmitter<void>();
  @Output() upload: EventEmitter<File> = new EventEmitter<File>();
  @Output() viewSample: EventEmitter<void> = new EventEmitter<void>();
  @Input() customStyle: any;

  @Input() allowOnlyXlsx: boolean | undefined;
  @Input() allowedFileTypeList: string[] = ['.csv'];
  @Input() errorFilePath?: string;
  @Input() fileType: string = '.csv';
  @Input() fileUploadList: any = [];
  @Input() hasViewSample?: boolean = true;
  @Input() label: string = '';
  @Input() showUploadButton: boolean = true;
  @Input() uploadErrors?: CustomError;
  @Input() uploadFilePath?: string;
  @Input() uploadType?: ViewSample;
  @Input() uploadedFileName?: string;
  @Input() uploadedFileUrl?: string;
  @Input() disabled: boolean = false;
  @Input() viewFileLabel?: string;

  isFileUploaded: boolean = false;
  selectedFile?: File;
  uploadForm: FormGroup;

  constructor(private formBuilder: FormBuilder, private validationService: ValidationService) {
    this.uploadForm = this.createFormGroup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update the validator if allowedFileTypeList changes
    if (changes.allowedFileTypeList) {
      this.uploadForm
        .get('file')
        ?.setValidators([this.fileTypeValidator(this.allowedFileTypeList)]);
      this.uploadForm.get('file')?.updateValueAndValidity();
    }

    // uploadErrors
    if (changes.uploadErrors != null && !changes.uploadErrors.isFirstChange()) {
      this.validationService.addResponseErrorToForm(this.uploadForm, this.uploadErrors!);
    }

    // uploadFilePath
    if (changes.uploadFilePath != null && !changes.uploadFilePath.isFirstChange()) {
      this.clearUpload();
      this.isFileUploaded = true;
    }

    // uploadType
    if (changes.uploadType != null && !changes.uploadType.isFirstChange()) {
      this.clearUpload();
    }

    // Set file name in input if uploadedFileUrl is present
    if (changes.uploadedFileUrl && this.uploadedFileUrl) {
      this.uploadForm.get('file')?.setValue(this.displayFileName);
      this.isFileUploaded = true;
    }
  }

  get displayFileName(): string {
    if (this.selectedFile) {
      return this.selectedFile.name;
    }
    if (this.uploadedFileUrl && this.uploadedFileName) {
      return this.uploadedFileName;
    }
    if (this.uploadedFileUrl) {
      try {
        return decodeURIComponent(
          this.uploadedFileUrl.split('/').pop()?.split('?')[0] || 'Uploaded file',
        );
      } catch {
        return 'Uploaded file';
      }
    }

    return '';
  }

  onChangeFile(e: any): void {
    this.isFileUploaded = false;
    this.uploadedFileUrl = '';

    const selectedFile: File = <File>e.target.files[0];
    if (selectedFile != null) {
      this.selectedFile = selectedFile;
      this.uploadForm.get(['file'])?.setValue(this.selectedFile?.name);
      this.uploadForm.get(['file'])?.markAsDirty();
      if (!this.showUploadButton) {
        this.upload.emit(this.selectedFile);
      }
    } else {
      this.upload.emit(selectedFile);
      this.clearUpload();
    }
  }

  onClickClear(): void {
    this.clearUpload();
    this.uploadedFileUrl = undefined;
    this.remove.emit();
  }

  onClickUpload(): void {
    this.upload.emit(this.selectedFile);
  }

  onClickViewSample(): void {
    this.viewSample.emit();
  }

  private clearUpload(): void {
    delete this.selectedFile;
    this.isFileUploaded = false;
    this.uploadedFileUrl = '';
    this.uploadForm.reset();
  }

  private createFormGroup(): FormGroup {
    return this.formBuilder.group({
      file: [null, [this.fileTypeValidator(this.allowedFileTypeList)]],
      fileInput: [null],
    });
  }

  private fileTypeValidator(allowedFileTypeList: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const filePath: string = control?.value;
      const fileType = filePath?.slice(filePath.lastIndexOf('.'));

      if (this.allowOnlyXlsx === true) {
        return fileType === '.xlsx'
          ? null
          : {
              [FormErrors.uploadType]: true,
            };
      }

      // No need for else block here
      return allowedFileTypeList.includes(fileType)
        ? null
        : {
            [FormErrors.uploadType]: true,
          };
    };
  }
}
